import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, ClipboardList, FileWarning, FolderOpen, RefreshCw, Share2 } from 'lucide-react'
import { useAuth } from '@core/auth/useAuth'
import { abrirBcfDaPartilha, abrirModeloDaPartilha, abrirTriagemDaPartilha } from './abrirPartilhado'
import { abrirJanelaPartilhar, recarregarComigo, useComigo } from './partilhasEstado'
import {
  doTipo,
  NOME_DO_TIPO,
  origemDoItem,
  pecasSoPorSessao,
  totalPartilhados,
  type ItemComigo,
  type TipoPartilha,
} from './partilhasRegras'

// ---------------------------------------------------------------------------
// "Partilhados comigo": o que outras pessoas partilharam com esta, vindo
// de outros workspaces (ou, para um convidado, tudo o que tem). A mesma
// lista serve a página própria (/:workspace/partilhados), a barra
// lateral do viewer, a biblioteca do BCF e o banco dos conflitos, com
// `tipo` a filtrar.
// ---------------------------------------------------------------------------

const ICONE: Record<TipoPartilha, typeof Box> = {
  modelo: Box,
  bcf: FileWarning,
  triagem: ClipboardList,
  sessao: FolderOpen,
}

interface Props {
  tipo?: TipoPartilha | null
  /** Sem cabeçalho grande nem texto de introdução (barra lateral). */
  compacto?: boolean
}

export default function PartilhadosComigo({ tipo = null, compacto = false }: Props) {
  const { mode, workspace, partilhados } = useAuth()
  const navigate = useNavigate()
  // A página lê sempre (pode haver partilhas novas desde a entrada); as
  // secções compactas só quando o /me disse que há alguma coisa.
  const ligado =
    mode === 'cloud' &&
    !!workspace &&
    (!compacto || totalPartilhados(partilhados) > 0 || !!workspace.convidado)
  const { lista, erro } = useComigo(ligado)
  const [aAbrir, setAAbrir] = useState<string | null>(null)
  if (!ligado || !workspace) return null
  const itens = doTipo(lista ?? [], tipo)
  const dentroDeSessoes = tipo === null ? pecasSoPorSessao(lista ?? []) : 0
  if (compacto && itens.length === 0) return null

  const abrir = async (i: ItemComigo) => {
    setAAbrir(i.id)
    try {
      if (i.tipo === 'sessao') {
        // A sessão inteira abre-se pelo endereço (o mesmo do email e do
        // aviso no sino): modelos, triagem, BCF e pontos de vista.
        navigate(`/${workspace.slug}/?sessao=${encodeURIComponent(i.id)}`)
      } else if (i.tipo === 'modelo') {
        navigate(`/${workspace.slug}/`)
        await abrirModeloDaPartilha(i)
      } else if (i.tipo === 'bcf') {
        const localId = abrirBcfDaPartilha(i)
        navigate(`/${workspace.slug}/bcf/${localId}`)
      } else {
        abrirTriagemDaPartilha(i)
        navigate(`/${workspace.slug}/`)
      }
    } finally {
      setAAbrir(null)
    }
  }

  return (
    <section
      data-testid="partilhados-comigo"
      className={compacto ? 'flex flex-col gap-1 text-xs' : 'flex flex-col gap-2 p-4 text-sm'}
    >
      <div className="flex items-center gap-2">
        <Share2 size={compacto ? 11 : 14} aria-hidden />
        <h2 className={compacto ? 'text-[11px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]' : 'font-medium'}>
          Partilhados comigo{tipo ? ` (${NOME_DO_TIPO[tipo]})` : ''}
        </h2>
        <button
          type="button"
          onClick={() => void recarregarComigo()}
          aria-label="Atualizar a lista de partilhados comigo"
          title="Atualizar"
          className="ml-auto rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={11} />
        </button>
      </div>
      {!compacto && (
        <p className="text-xs text-[var(--color-text-dim)]">
          {workspace.convidado
            ? 'Tem uma conta de convidado: vê apenas o que lhe partilharam. Uma sessão abre tudo de uma vez (modelos, conflitos, BCF e pontos de vista); também pode abrir um modelo no viewer, um BCF na lista de tópicos ou uma triagem no banco dos conflitos.'
            : 'O que outras pessoas partilharam consigo. Uma sessão abre tudo de uma vez (modelos, conflitos, BCF e pontos de vista). O que é deste workspace, fora das sessões, aparece nas listas dele.'}
        </p>
      )}
      {dentroDeSessoes > 0 && (
        <p className="text-[10px] text-[var(--color-text-dim)]">
          Mais {dentroDeSessoes} {dentroDeSessoes === 1 ? 'peça' : 'peças'} (modelos, triagens ou BCF) que
          só vê por fazerem parte de uma sessão: abrem-se com a sessão.
        </p>
      )}
      {erro && <p className="text-xs text-[var(--color-error-text)]">{erro}</p>}
      {lista === null ? (
        <p className="text-xs text-[var(--color-text-dim)]">A ler…</p>
      ) : itens.length === 0 ? (
        <p className="text-xs text-[var(--color-text-dim)]">Ainda não há nada partilhado consigo.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {itens.map((i) => {
            const Icone = ICONE[i.tipo]
            return (
              <li
                key={i.id}
                className="flex items-center gap-2 rounded border border-[var(--color-border-subtle)] px-2 py-1 text-xs"
              >
                <Icone size={12} aria-hidden className="shrink-0 text-[var(--color-text-dim)]" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-[var(--color-text)]" title={i.nome ?? undefined}>
                    {i.nome ?? NOME_DO_TIPO[i.tipo]}
                  </div>
                  <div className="truncate text-[10px] text-[var(--color-text-dim)]">
                    {tipo ? '' : `${NOME_DO_TIPO[i.tipo]} · `}
                    {origemDoItem(i)}
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  title="Quem mais tem acesso"
                  onClick={() =>
                    abrirJanelaPartilhar({ tipo: i.tipo, nome: i.nome ?? NOME_DO_TIPO[i.tipo], partilhaId: i.id })
                  }
                >
                  Acessos
                </button>
                <button
                  type="button"
                  disabled={aAbrir === i.id}
                  onClick={() => void abrir(i)}
                  className="rounded border border-[var(--color-accent-border)] px-1.5 py-0 text-[10px] text-[var(--color-accent)] hover:bg-[var(--color-accent-bg)] disabled:opacity-50"
                >
                  {aAbrir === i.id ? 'A abrir…' : 'Abrir'}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
